import React from "react";

export interface TierConnection {
  fromIndex: number;
  toIndex: number;
  id?: string;
  isActive?: boolean;
  isUnlocked?: boolean; // Parent talent has level > 0
  isLocked?: boolean;   // Target talent is locked (building requirement not met)
}

interface ConnectionPath {
  id: string;
  path: string;
  isActive?: boolean;
  isUnlocked?: boolean;
  isLocked?: boolean;
}

interface ResearchTreeConnectionsProps {
  currentTierCount: number;
  nextTierCount: number;
  connections?: TierConnection[];
  activePath?: string; // Optional: ID of the active connection path
  minWidth?: number;
  cardWidth?: number;
  cardGap?: number;
}

const ResearchTreeConnections: React.FC<ResearchTreeConnectionsProps> = ({
  currentTierCount,
  nextTierCount,
  connections = [],
  activePath,
  minWidth,
  cardWidth = 250,
  cardGap = 30,
}) => {
  const generateConnectionPaths = (): ConnectionPath[] => {
    const paths: ConnectionPath[] = [];

    if (!currentTierCount || !nextTierCount || !connections.length) {
      return paths;
    }

    const tierWidth = minWidth || 480;

    // Calculate the actual width occupied by cards in each tier
    const getCurrentTierCardsWidth = () => 
      currentTierCount * cardWidth + Math.max(0, currentTierCount - 1) * cardGap;
    const getNextTierCardsWidth = () => 
      nextTierCount * cardWidth + Math.max(0, nextTierCount - 1) * cardGap;

    // Calculate the starting offset to center the cards
    const currentTierOffset = (tierWidth - getCurrentTierCardsWidth()) / 2;
    const nextTierOffset = (tierWidth - getNextTierCardsWidth()) / 2;

    // Calculate card center position as percentage of tier width
    const getCurrentPosition = (index: number): number => {
      const cardCenterX = currentTierOffset + index * (cardWidth + cardGap) + cardWidth / 2;
      return (cardCenterX / tierWidth) * 100;
    };

    const getNextPosition = (index: number): number => {
      const cardCenterX = nextTierOffset + index * (cardWidth + cardGap) + cardWidth / 2;
      return (cardCenterX / tierWidth) * 100;
    };

    // Generate connection paths only for defined parent-child pairs
    connections.forEach((connection, index) => {
      const startX = getCurrentPosition(connection.fromIndex);
      const endX = getNextPosition(connection.toIndex);

      const pathData = `
        M ${startX} 0
        L ${startX} 40
        L ${endX} 40
        L ${endX} 80
      `;

      const pathId =
        connection.id ??
        `connection-${connection.fromIndex}-${connection.toIndex}-${index}`;

      paths.push({
        id: pathId,
        path: pathData,
        isActive: connection.isActive || activePath === pathId,
        isUnlocked: connection.isUnlocked,
        isLocked: connection.isLocked,
      });
    });

    return paths;
  };

  const connectionPaths = generateConnectionPaths();

  if (!connectionPaths.length) {
    return null;
  }

  const connectionStyle = minWidth
    ? { minWidth: `${minWidth}px` }
    : undefined;

  return (
    <div className="tier-connections" style={connectionStyle}>
      <svg
        className="connections-svg"
        width="100%"
        height="80"
        viewBox="0 0 100 80"
        preserveAspectRatio="none"
        style={connectionStyle}
      >
        <defs>
          {/* Default gradient */}
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#d4a574" />
            <stop offset="50%" stopColor="#be863e" />
            <stop offset="100%" stopColor="#8b6332" />
          </linearGradient>

          {/* Active/highlighted gradient */}
          <linearGradient
            id="activeLineGradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor="#ffd700" />
            <stop offset="50%" stopColor="#ffed4e" />
            <stop offset="100%" stopColor="#ffa500" />
          </linearGradient>

          {/* Unlocked/glowing gradient - YELLOW */}
          <linearGradient
            id="unlockedLineGradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor="#ffd700" />
            <stop offset="50%" stopColor="#f5c400" />
            <stop offset="100%" stopColor="#e6b000" />
          </linearGradient>

          {/* Locked/dimmed gradient - LIGHT GRAY */}
          <linearGradient
            id="lockedLineGradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor="#a0a0a0" />
            <stop offset="50%" stopColor="#888888" />
            <stop offset="100%" stopColor="#707070" />
          </linearGradient>

          {/* Glow filter for unlocked paths */}
          <filter id="glowFilter" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>

          <defs>
            <linearGradient
              id="arrowGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="#d4a574" />
              <stop offset="100%" stopColor="#8b6332" />
            </linearGradient>
          </defs>

          <marker
            id="arrowhead"
            markerWidth="16"
            markerHeight="16"
            refX="13"
            refY="8"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <polygon
              points="2,3 13,8 2,13"
              fill="url(#arrowGradient)"
              stroke="#8b6332"
              strokeWidth="0.5"
              strokeLinejoin="round"
            />
          </marker>

          {/* Arrow marker for active state */}
          <marker
            id="arrowheadActive"
            markerWidth="16"
            markerHeight="16"
            refX="13"
            refY="8"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <polygon
              points="2,3 13,8 2,13"
              fill="#be863e"
              stroke="#8b6332"
              strokeWidth="0.5"
              strokeLinejoin="round"
            />
          </marker>

          {/* Arrow marker for unlocked state - YELLOW */}
          <marker
            id="arrowheadUnlocked"
            markerWidth="16"
            markerHeight="16"
            refX="13"
            refY="8"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <polygon
              points="2,3 13,8 2,13"
              fill="#f5c400"
              stroke="#e6b000"
              strokeWidth="0.5"
              strokeLinejoin="round"
            />
          </marker>

          {/* Arrow marker for locked state - LIGHT GRAY */}
          <marker
            id="arrowheadLocked"
            markerWidth="16"
            markerHeight="16"
            refX="13"
            refY="8"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <polygon
              points="2,3 13,8 2,13"
              fill="#888888"
              stroke="#707070"
              strokeWidth="0.5"
              strokeLinejoin="round"
            />
          </marker>
        </defs>

        {connectionPaths.map((connection) => {
          // Determine the appropriate gradient and marker based on state
          const getStrokeGradient = () => {
            if (connection.isActive) return "url(#activeLineGradient)";
            if (connection.isLocked) return "url(#lockedLineGradient)";
            if (connection.isUnlocked) return "url(#unlockedLineGradient)";
            return "url(#lineGradient)";
          };

          const getMarker = () => {
            if (connection.isActive) return "url(#arrowheadActive)";
            if (connection.isLocked) return "url(#arrowheadLocked)";
            if (connection.isUnlocked) return "url(#arrowheadUnlocked)";
            return "url(#arrowhead)";
          };

          const getOutlineColor = () => {
            if (connection.isLocked) return "#606060";
            if (connection.isUnlocked) return "#c9a000";
            return "#8b6332";
          };

          const getClassName = () => {
            const classes = ["connection-line"];
            if (connection.isActive) classes.push("active");
            else if (connection.isLocked) classes.push("locked");
            else if (connection.isUnlocked) classes.push("unlocked");
            else if (activePath) classes.push("inactive");
            return classes.join(" ");
          };

          return (
            <g key={connection.id}>
              {/* Glow effect for unlocked paths - YELLOW */}
              {connection.isUnlocked && !connection.isLocked && (
                <path
                  d={connection.path}
                  stroke="#ffd700"
                  strokeWidth="6"
                  fill="none"
                  opacity="0.4"
                  vectorEffect="non-scaling-stroke"
                  filter="url(#glowFilter)"
                  className="connection-glow"
                />
              )}
              {/* Shadow/outline path */}
              <path
                d={connection.path}
                stroke={getOutlineColor()}
                strokeWidth="2"
                fill="none"
                opacity={connection.isLocked ? "0.5" : "1"}
                vectorEffect="non-scaling-stroke"
                className={getClassName()}
              />
              {/* Main gradient path */}
              <path
                d={connection.path}
                stroke={getStrokeGradient()}
                strokeWidth="2"
                fill="none"
                vectorEffect="non-scaling-stroke"
                markerEnd={getMarker()}
                opacity={connection.isLocked ? "0.5" : "1"}
                className={getClassName()}
                filter={connection.isUnlocked && !connection.isLocked ? "url(#glowFilter)" : undefined}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default ResearchTreeConnections;
